import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {v4} from "uuid";

import {RootState} from "../../../app/store";
import {RequestStatus} from "../../../api/apiConfiguration";
import {FranchiseRequestPartial, createFranchiseRequest} from "./franchiseSlice";

export type CreateFranchiseRequestPartial = FranchiseRequestPartial;

export interface CreateFranchiseState {
    createFranchiseRequest: CreateFranchiseRequestPartial;
    createFranchiseRequestStatus: RequestStatus;
}

const buildDefaultCreateFranchiseRequest = (): CreateFranchiseRequestPartial => ({
    publicId: v4(),
    publicLocationIdList: [],
    franchiseEmailTranscriptRecipients: "",
    franchiseEmailFiltrationEnabled: false,
    franchiseEmailTranscriptSummaryEnabled: false,
    focusEnabled: true,
    sharedWithFocusedFranchises: false
});

const buildInitialState = (): CreateFranchiseState => ({
    createFranchiseRequest: buildDefaultCreateFranchiseRequest(),
    createFranchiseRequestStatus: RequestStatus.NONE
});

const initialState: CreateFranchiseState = buildInitialState();

export const createFranchise = createAsyncThunk<void, undefined, { state: RootState }>(
    "franchiseCreate/create",
    (_payload, {getState}) => createFranchiseRequest(getState().franchiseCreate.createFranchiseRequest)
);

export const franchiseCreateSlice = createSlice({
    name: "franchiseCreate",
    initialState,
    reducers: {
        clearCreateFranchiseState: () => buildInitialState(),
        updateCreateFranchiseRequest: (state, {payload}: PayloadAction<CreateFranchiseRequestPartial>) => {
            state.createFranchiseRequest = {
                ...state.createFranchiseRequest,
                ...payload
            };
        }
    },
    extraReducers: builder => {
        builder.addCase(createFranchise.pending, state => {
            state.createFranchiseRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(createFranchise.fulfilled, state => {
            state.createFranchiseRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(createFranchise.rejected, state => {
            state.createFranchiseRequestStatus = RequestStatus.REJECTED;
            state.createFranchiseRequest.publicId = v4();
        });
    }
});

export const {
    clearCreateFranchiseState,
    updateCreateFranchiseRequest
} = franchiseCreateSlice.actions;

export const createFranchiseRequestSelector = (state: RootState): CreateFranchiseRequestPartial =>
    state.franchiseCreate.createFranchiseRequest;
export const createFranchiseRequestStatusSelector = (state: RootState): RequestStatus =>
    state.franchiseCreate.createFranchiseRequestStatus;
